import type { Banco } from '@sheafyield/sync';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import {
  banco as bancoDoApp,
  cliente as clienteDoApp,
  motor as motorDoApp,
} from '../app/contexto.js';
import {
  minutosDeHoje,
  minutosDoMesCorrente,
  sessaoAberta,
  type MinutosPorBalde,
} from '../dados/consultas.js';
import { donoDaSessao, type DonoDasLinhas } from '../dados/dono.js';
import { useConsultaCom, type MotorObservavel } from '../dados/useConsulta.js';
import { Icone } from '../ds/icones.js';
import { Botao } from '../ds/Botao.js';
import { FaixaDeInstalacao } from '../pwa/FaixaDeInstalacao.js';
import { CronometroCom } from './Cronometro.js';
import { TiraParaVisitar } from './TiraParaVisitar.js';
import { VisitasDoDia } from './VisitasDoDia.js';
import {
  duracaoPorExtenso,
  formatarDuracao,
  useDiaLocal,
  useViradaDeMinuto,
} from './relogio.js';
import './hoje.css';

/** O dono do app, amarrado uma vez — o cliente é singleton (mesma amarração
 *  do `Cronometro`). */
const donoDoApp = donoDaSessao(clienteDoApp);

export interface DepsDeHoje {
  banco: Banco;
  motor: MotorObservavel;
  dono: DonoDasLinhas;
  /** O que o botão largo faz. Injetado porque quem o resolve é a rota
   *  (`/hoje?registrar=1`), e a folha Registrar chega na etapa seguinte:
   *  assim a tela se testa sem roteador em volta. */
  aoRegistrar: () => void;
  /** O mesmo, para uma linha da tira "Para visitar hoje": a folha abre já
   *  ligada àquela ficha (`/hoje?registrar=1&pessoa=…`). */
  aoRegistrarPessoa: (id: string) => void;
}

interface Horas {
  dia: MinutosPorBalde;
  mes: MinutosPorBalde;
}

/**
 * As horas do dia e do mês, os dois números que o usuário confere antes de
 * fechar o relatório — e que agora **são** os do relatório.
 *
 * O número grande de cada cartão é o **tempo de campo**; os créditos (LDC,
 * Betel, escola, socorro) saem numa linha à parte, e só quando existem. Os
 * dois cartões somavam tudo junto, sem o teto de 55 h, enquanto este briefing
 * os apresentava como conferíveis contra o relatório — que separa os dois e
 * limita os créditos. Quem cronometra em "Crédito", que é justamente por que
 * a categoria existe, via na tela um total que o relatório nunca ia mostrar.
 *
 * Sem crédito nenhum no período — o caso da esmagadora maioria — os cartões
 * ficam exatamente como estavam: um rótulo e um número.
 *
 * A releitura por minuto entra pela **chave** da consulta, porque é a chave
 * que a faz rodar de novo. Trocar a chave zera o resultado (é o contrato do
 * `useConsulta`: chave nova, recorte novo), e isso pintaria "…" por um
 * quadro a cada minuto — daí o último valor lido ficar guardado. O ajuste é
 * durante o render, que é a forma que o React recomenda para estado derivado
 * de prop, e termina numa passada: assim que `ultimas` recebe o objeto lido,
 * a comparação por identidade não dispara mais.
 *
 * O dia entra na mesma chave, e por um motivo diferente: o tique só existe
 * com sessão aberta, então, sem ele, à meia-noite o cartão "Hoje" continuaria
 * mostrando os minutos de ontem (ver `useDiaLocal`). Ele chega por prop —
 * quem o mantém é a `HojeTelaCom`, uma vez para a tela toda.
 *
 * O tique marca **na virada**, e não ao montar: o total vem da releitura do
 * banco, que a consulta acabou de fazer com a chave atual, e marcar ao
 * montar mandaria reler o que já se leu a cada montagem da tela. É a única
 * coisa em que este tique difere do do cronômetro (ver `useViradaDeMinuto`).
 */
function Horas({
  banco,
  motor,
  dia,
}: {
  banco: Banco;
  motor: MotorObservavel;
  dia: string;
}) {
  const { dados: aberta } = useConsultaCom(
    motor,
    'sessao-aberta',
    async () => (await sessaoAberta(banco)) ?? null,
  );
  const tique = useViradaDeMinuto(
    aberta === undefined || aberta === null ? null : Date.parse(aberta.inicio),
    'na virada',
  );
  const consulta = useConsultaCom(
    motor,
    `horas-${dia}-${String(tique)}`,
    async () => {
      const agora = new Date();
      return {
        dia: await minutosDeHoje(banco, agora),
        mes: await minutosDoMesCorrente(banco, agora),
      };
    },
  );
  const [ultimas, setUltimas] = useState<Horas | null>(null);
  if (consulta.dados !== undefined && consulta.dados !== ultimas) {
    setUltimas(consulta.dados);
  }
  const horas = consulta.dados ?? ultimas;

  // Mesma regra da tira e das visitas: o recado só entra quando não há nada
  // lido para mostrar. Sem ele, uma primeira leitura que falhasse deixaria os
  // dois cartões em "…" para sempre, sem dizer nada a ninguém — nem na tela,
  // nem no leitor de tela.
  if (consulta.erro !== null && horas === null) {
    return (
      <p className="hoje__erro" role="alert">
        Não deu para ler as horas: {consulta.erro.message}
      </p>
    );
  }

  return (
    <dl className="hoje__horas">
      <Hora rotulo="Hoje" balde={horas?.dia} />
      <Hora rotulo="No mês" balde={horas?.mes} />
    </dl>
  );
}

/** Um dos dois cartões. O que se vê é `"1h07"`, com `tabular-nums` para não
 *  dançar a cada minuto; o que o VoiceOver lê é "1 hora e 7 minutos", porque
 *  "1h07" ele soletra.
 *
 *  A segunda linha só existe quando há crédito no período. Ela é
 *  `aria-hidden` porque o texto do leitor de tela já diz as duas parcelas
 *  numa frase só: ouvir "mais 45 minutos de crédito" depois de já ter ouvido
 *  a frase inteira seria a mesma informação duas vezes. */
function Hora({
  rotulo,
  balde,
}: {
  rotulo: string;
  balde: MinutosPorBalde | undefined;
}) {
  return (
    <div className="hoje__hora">
      <dt className="hoje__hora-rotulo">{rotulo}</dt>
      <dd className="hoje__hora-valor">
        {balde === undefined ? (
          <span>…</span>
        ) : (
          <>
            <span aria-hidden="true">{formatarDuracao(balde.campo)}</span>
            <span className="visualmente-oculto">
              {duracaoPorExtenso(balde.campo)} de campo
              {balde.creditos > 0
                ? ` e ${duracaoPorExtenso(balde.creditos)} de crédito`
                : ''}
            </span>
            {balde.creditos > 0 ? (
              <span className="hoje__hora-credito" aria-hidden="true">
                {`+ ${formatarDuracao(balde.creditos)} crédito`}
              </span>
            ) : null}
          </>
        )}
      </dd>
    </div>
  );
}

/**
 * A tela Hoje, de cima para baixo: a faixa de instalação (só fora de
 * standalone, e só aqui — spec §8), o cronômetro, as horas do dia e do mês,
 * quem visitar hoje, as visitas já registradas e o botão largo "Registrar
 * visita".
 *
 * O botão fica por último **em DOM** — é o fim da leitura da tela para o
 * VoiceOver — e **preso à borda de baixo** enquanto a tela rola. Ele era só
 * de fluxo, e isso não cumpria o critério da spec §8: em AX5 a faixa de
 * instalação, o cronômetro, os dois cartões de horas e os dois títulos já
 * passam da altura da tela **com o banco vazio**, então o botão nascia
 * abaixo da dobra e o primeiro dos dois toques exigia rolar para achá-lo.
 * Com dez visitas ele afundava mais.
 *
 * `sticky` e não `fixed`: assim ele continua sendo um filho da pilha, some
 * junto com a tela e não vira mais um `position: fixed` dentro de
 * `.tela { overflow: clip }`. O `clip` (e não `hidden`) da `.tela` é o que
 * deixa o `sticky` resolver contra o viewport — é a mesma razão pela qual o
 * `.cabecalho` gruda no topo.
 *
 * A altura da barra de abas e o aviso de Desfazer entram no cálculo do
 * deslocamento (ver `hoje.css`): o botão fica acima dos dois, não sob eles.
 *
 * Esta é a forma injetável, para teste. A rota usa `HojeTela`.
 */
export function HojeTelaCom({
  banco,
  motor,
  dono,
  aoRegistrar,
  aoRegistrarPessoa,
}: DepsDeHoje) {
  // Um só para os três blocos que recortam o dia. Eram três chamadas
  // independentes de `useDiaLocal` — três temporizadores até a meia-noite e
  // três ouvintes de `visibilitychange` para o mesmo valor. Não vazava (cada
  // um limpava o seu), mas era o mesmo estado reinventado três vezes, e
  // nada garantia que os três virassem o dia no mesmo render.
  const dia = useDiaLocal();
  return (
    // Sem `h1`: quem o carrega é o cabeçalho preso da `Casca`.
    <div className="hoje">
      <FaixaDeInstalacao />
      <CronometroCom banco={banco} motor={motor} dono={dono} />
      <Horas banco={banco} motor={motor} dia={dia} />
      <TiraParaVisitar
        banco={banco}
        motor={motor}
        dia={dia}
        aoRegistrarPessoa={aoRegistrarPessoa}
      />
      <VisitasDoDia banco={banco} motor={motor} dia={dia} />
      <div className="hoje__acao">
        <Botao largura="cheia" onClick={aoRegistrar}>
          <Icone>
            <path d="M12 3v18M3 12h18" />
          </Icone>
          <span>Registrar visita</span>
        </Botao>
      </div>
    </div>
  );
}

/** `HojeTelaCom` amarrada ao banco, ao motor e à sessão do app. */
export function HojeTela() {
  const navegar = useNavigate();
  return (
    <HojeTelaCom
      banco={bancoDoApp}
      motor={motorDoApp}
      dono={donoDoApp}
      aoRegistrar={() => {
        // O endereço é o que abre a folha, e não um estado local: é o mesmo
        // caminho do atalho da Tela de Início (`/registrar` redireciona para
        // cá), e voltar fecha a folha sem código nenhum.
        void navegar({ to: '/hoje', search: { registrar: 1 } });
      }}
      aoRegistrarPessoa={(id) => {
        void navegar({ to: '/hoje', search: { registrar: 1, pessoa: id } });
      }}
    />
  );
}
